import { useState } from 'react';
import { createVendor, updateVendor } from '../../graphql/companyResourceQueries';
import { formatPhoneNumber, handlePhoneChange } from '../../utils/phoneFormat';
import { Btn, BtnIconOnly, IconClose, InputField } from '../ui';

interface VendorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => Promise<void>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editVendor: any | null;
    companyId: string;
    initialName?: string;
}

export const VendorModal = ({ isOpen, onClose, onSave, editVendor, companyId, initialName = '' }: VendorModalProps) => {
    const [name, setName] = useState(editVendor?.name || initialName);
    const [address, setAddress] = useState(editVendor?.address || '');
    const [phoneNumber, setPhoneNumber] = useState(formatPhoneNumber(editVendor?.phoneNumber || ''));
    const [faxNumber, setFaxNumber] = useState(formatPhoneNumber(editVendor?.faxNumber || ''));
    const [repName, setRepName] = useState(editVendor?.repName || '');
    const [repPhoneNumber, setRepPhoneNumber] = useState(formatPhoneNumber(editVendor?.repPhoneNumber || ''));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    if (!isOpen) return null;

    const validate = () => {
        const errs: Record<string, string> = {};
        if (!name.trim()) errs.name = 'Vendor name is required';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const input: any = {
                companyId,
                name: name.trim(),
                address: address.trim() || null,
                phoneNumber: phoneNumber.trim() || null,
                faxNumber: faxNumber.trim() || null,
                repName: repName.trim() || null,
                repPhoneNumber: repPhoneNumber.trim() || null,
            };

            if (editVendor) {
                await updateVendor({ id: editVendor.id, ...input });
            } else {
                await createVendor(input);
            }
            await onSave();
        } catch (err) {
            console.error('Error saving vendor:', err);
            setErrors({ _form: 'Failed to save. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="cr-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="cr-modal" onClick={e => e.stopPropagation()}>
                <div className="cr-modal-header">
                    <h2 className="cr-modal-title">{editVendor ? 'Edit Vendor' : 'Add Vendor'}</h2>
                    <BtnIconOnly size="sm" icon={<IconClose />} ariaLabel="Close" onClick={onClose} />
                </div>
                <div className="cr-modal-body">
                    {errors._form && <div className="cr-error-banner">⚠️ {errors._form}</div>}

                    <div className="cr-field">
                        <InputField
                            label="Vendor Name *"
                            inputId="vendor-name"
                            value={name}
                            onChange={setName}
                            hasError={!!errors.name}
                            ariaDescribedBy={errors.name ? 'vendor-name-error' : 'vendor-name-hint'}
                        />
                        {errors.name && <div id="vendor-name-error" className="cr-field-error-text">{errors.name}</div>}
                        <div id="vendor-name-hint" className="cr-field-helper">
                            Address, phone, fax, and representative can be added now or later.
                        </div>
                    </div>

                    <div className="cr-field">
                        <InputField label="Address" inputId="vendor-address" value={address} onChange={setAddress} />
                    </div>

                    <div className="cr-field">
                        <InputField
                            label="Phone Number"
                            inputId="vendor-phone"
                            type="tel"
                            value={phoneNumber}
                            onChange={(value) => handlePhoneChange(value, setPhoneNumber)}
                            placeholder="(xxx) xxx-xxxx"
                        />
                    </div>

                    <div className="cr-field">
                        <InputField
                            label="Fax Number"
                            inputId="vendor-fax"
                            type="tel"
                            value={faxNumber}
                            onChange={(value) => handlePhoneChange(value, setFaxNumber)}
                            placeholder="(xxx) xxx-xxxx"
                        />
                    </div>

                    <div className="cr-field">
                        <InputField label="Representative Name" inputId="vendor-rep" value={repName} onChange={setRepName} />
                    </div>

                    <div className="cr-field">
                        <InputField
                            label="Representative Phone"
                            inputId="vendor-rep-phone"
                            type="tel"
                            value={repPhoneNumber}
                            onChange={(value) => handlePhoneChange(value, setRepPhoneNumber)}
                            placeholder="(xxx) xxx-xxxx"
                        />
                    </div>
                </div>
                <div className="cr-modal-footer">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : (editVendor ? 'Update' : 'Create')}
                        buttonOnClick={handleSubmit}
                        buttonDisabled={saving}
                    />
                </div>
            </div>
        </div>
    );
};
